import React, { useCallback, useEffect, useRef, useState } from "react";
import { ClipboardList, AlertTriangle, Check, RefreshCw, Loader2, Store, Banknote, BellRing, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { adminApi, formatDate, errorMessage } from "../../lib/api";
import { startRinging, stopRinging, unlockAudio } from "../../lib/chime";

const TABS = [
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];

const TYPE_META = {
  restaurant_no_accept: { icon: Store, label: "Order not accepted" },
  restaurant_repeat_rejections: { icon: AlertTriangle, label: "Reliability concern" },
  driver_negative_payout: { icon: Banknote, label: "Driver owes cash" },
};

export default function InternalTasks() {
  const [tab, setTab] = useState("open");
  const [tasks, setTasks] = useState(null);
  const [openCount, setOpenCount] = useState(0);
  const [busy, setBusy] = useState("");
  const [ringing, setRinging] = useState(false);
  const knownOpen = useRef(null);

  const load = useCallback(() => {
    adminApi.internalTasks(tab).then((d) => {
      setTasks(d.tasks || []);
      setOpenCount(d.open_count || 0);
    }).catch((e) => { setTasks([]); if (e?.response?.status !== 400) toast.error(errorMessage(e)); });
  }, [tab]);

  // Poll for new open tasks and ring the staff when one appears.
  useEffect(() => {
    let active = true;
    const poll = () => adminApi.internalTaskCounts().then((d) => {
      if (!active) return;
      const n = d.open || 0;
      setOpenCount(n);
      if (knownOpen.current !== null && n > knownOpen.current) {
        unlockAudio(); startRinging(); setRinging(true);
        toast.warning("New staff task", { description: "An order needs attention or a driver owes cash." });
      }
      knownOpen.current = n;
      if (tab === "open") load();
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 12000);
    return () => { active = false; clearInterval(t); };
  }, [tab, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => stopRinging(), []);

  const stopAlert = () => { stopRinging(); setRinging(false); };
  const resolve = async (id) => {
    setBusy(id);
    try { await adminApi.resolveInternalTask(id); toast.success("Task resolved"); stopAlert(); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };

  return (
    <>
      <PageHeader title="Internal tasks" description="Operational alerts for the staff team — unaccepted orders and driver cash shortfalls." actions={
        <div className="flex items-center gap-2">
          {ringing && <Button onClick={stopAlert} variant="outline" className="rounded-full border-amber-300 bg-amber-50 text-amber-700" data-testid="stop-alert"><BellRing className="mr-2 h-4 w-4" /> Stop alert</Button>}
          <Button variant="outline" onClick={load} className="rounded-full" data-testid="tasks-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
        </div>
      } />
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <StatCard label="Open tasks" value={openCount} icon={AlertTriangle} tone={openCount ? "brand" : "ink"} />
        <StatCard label="Showing" value={(tasks || []).length} hint={tab} icon={ClipboardList} tone="graphite" />
      </div>
      <div className="mb-5 flex gap-2" data-testid="task-tabs">
        {TABS.map((t) => <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-ink text-surface" : "bg-white text-ink-soft ring-1 ring-surface-300 hover:text-ink"}`} data-testid={`task-tab-${t.key}`}>{t.label}</button>)}
      </div>
      {tasks === null && <LoadingBlock rows={3} />}
      {tasks && tasks.length === 0 && <EmptyState icon={ClipboardList} title="Nothing here" text="New operational tasks will appear here automatically." />}
      <div className="grid gap-4" data-testid="internal-tasks">
        {(tasks || []).map((t) => {
          const meta = TYPE_META[t.type] || { icon: ClipboardList, label: t.type };
          const Icon = meta.icon;
          return (
            <div key={t.task_id} className={`kt-card p-5 ${t.status === "open" ? "ring-1 ring-amber-200" : "opacity-80"}`} data-testid={`task-${t.task_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full ${t.severity === "high" ? "bg-amber-100 text-amber-700" : "bg-surface-200 text-ink-soft"}`}><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="font-semibold text-ink">{t.title}</p>
                    <p className="mt-1 max-w-2xl text-sm text-ink-soft">{t.detail}</p>
                    <p className="mt-1 text-xs text-ink-muted">{meta.label} · {formatDate(t.created_at)}{t.assigned_name ? ` · Assigned to ${t.assigned_name}` : t.status === "open" ? " · Unassigned (queued)" : ""}{t.status === "resolved" && t.resolved_by_name ? ` · Resolved by ${t.resolved_by_name}` : ""}</p>
                  </div>
                </div>
                {t.status === "open" ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {t.meta?.phone && <a href={`tel:${t.meta.phone}`} className="inline-flex items-center rounded-full bg-graphite px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-graphite-600" data-testid={`call-owner-${t.task_id}`}><Phone className="mr-2 h-4 w-4" /> Call owner</a>}
                    <Button onClick={() => resolve(t.task_id)} disabled={busy === t.task_id} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid={`resolve-${t.task_id}`}>{busy === t.task_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Mark done</Button>
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-graphite-50 px-3 py-1 text-xs font-semibold text-graphite"><Check className="h-3.5 w-3.5" /> Resolved</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
